import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, 
  MapPin, 
  Globe, 
  Share2, 
  FileText, 
  CheckCircle, 
  AlertCircle,
  ArrowRight,
  ArrowLeft,
  Upload,
  X,
  Facebook,
  Linkedin
} from 'lucide-react';
import { authService } from '../../services/auth.service';
import { useTranslation } from 'react-i18next';
import logo from '../../assets/logo.png';
import categoriesService, { Category } from '../../services/categories.service';
import countriesService, { Country, Wilaya } from '../../services/countries.service';

interface FormData {
  about: string;
  address: string;
  website: string;
  facebook_url: string;
  linkedin_url: string;
  category_id: string;
  country: string;
  wilaya_id: number | null;
  city: string;
  logo?: File | null;
}

interface FormErrors {
  [key: string]: string;
}

const TrainingCenterCompleteProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    about: '',
    address: '',
    website: '',
    facebook_url: '',
    linkedin_url: '',
    category_id: '',
    country: '',
    wilaya_id: null,
    city: '',
    logo: null,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [successMessage, setSuccessMessage] = useState('');
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [countries, setCountries] = useState<Country[]>([]);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [wilayas, setWilayas] = useState<Wilaya[]>([]);
  const [loadingWilayas, setLoadingWilayas] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        // Load categories and countries in parallel
        const [categoriesData, countriesData] = await Promise.all([
          categoriesService.getCategories(),
          countriesService.getCountries()
        ]);
        
        setCategories(categoriesData);
        setCountries(countriesData);
      } catch (error) {
      } finally {
        setLoadingCategories(false);
        setLoadingCountries(false);
      }
    };

    loadData();
  }, []);

  // Load wilayas when Algeria is selected
  useEffect(() => {
    const loadWilayas = async () => {
      if (formData.country === 'Algeria') {
        setLoadingWilayas(true);
        try {
          // Find Algeria's ID
          const algeria = countries.find(country => country.name === 'Algeria');
          if (algeria) {
            const algerianWilayas = await countriesService.getWilayas(algeria.id);
            setWilayas(algerianWilayas);
          }
        } catch (error) {
        } finally {
          setLoadingWilayas(false);
        }
      } else {
        setWilayas([]);
      }
    };

    loadWilayas();
  }, [formData.country, countries]);

  

  const handleInputChange = (field: keyof FormData, value: string | number | number[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleCategoryChange = (categoryId: string) => {
    setFormData(prev => ({
      ...prev,
      category_id: categoryId
    }));
  };

  const handleCountryChange = (countryName: string) => {
    setFormData(prev => ({
      ...prev,
      country: countryName,
      wilaya_id: countryName === 'Algeria' ? prev.wilaya_id : null, // Reset wilaya if not Algeria
      city: countryName === 'Algeria' ? '' : prev.city // Reset city if Algeria
    }));
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData(prev => ({ ...prev, logo: file }));
      
      // Create preview
      const reader = new FileReader();
      reader.onload = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeLogo = () => {
    setFormData(prev => ({ ...prev, logo: null }));
    setLogoPreview(null);
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.about.trim()) {
      newErrors.about = 'Une description de votre centre est requise';
    } else if (formData.about.length < 50) {
      newErrors.about = 'La description doit contenir au moins 50 caractères';
    }

    if (!formData.address.trim()) {
      newErrors.address = 'L\'adresse de votre centre est requise';
    }

    if (!formData.country) {
      newErrors.country = 'Veuillez sélectionner un pays';
    }

    if (formData.country === 'Algeria' && !formData.wilaya_id) {
      newErrors.wilaya_id = 'Veuillez sélectionner une wilaya';
    }

    if (formData.country !== 'Algeria' && !formData.city.trim()) {
      newErrors.city = 'La ville est requise pour les pays autres que l\'Algérie';
    }

    if (!formData.category_id) {
      newErrors.category_id = 'Veuillez sélectionner une catégorie d\'expertise';
    }

    if (formData.website && !formData.website.match(/^https?:\/\/.+/)) {
      newErrors.website = t('auth.trainingCenter.validation.websiteInvalid');
    }

    if (formData.facebook_url && !formData.facebook_url.match(/^https?:\/\/(www\.)?(facebook\.com|fb\.com)\/.+/)) {
      newErrors.facebook_url = t('auth.trainingCenter.validation.facebookInvalid');
    }

    if (formData.linkedin_url && !formData.linkedin_url.match(/^https?:\/\/(www\.)?linkedin\.com\/(in|company)\/.+/)) {
      newErrors.linkedin_url = t('auth.trainingCenter.validation.linkedinInvalid');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);
    
    try {
      // Get current user info
      const user = authService.getUser();
      if (!user || !user.id) {
        throw new Error('Utilisateur non authentifié');
      }

      // Find the country ID from the selected country name
      const selectedCountry = countries.find(country => country.name === formData.country);
      if (!selectedCountry) {
        throw new Error('Pays sélectionné introuvable');
      }
      const country_id = selectedCountry.id;

      // Prepare update data
      const updateData = {
        about: formData.about,
        address: formData.address,
        website: formData.website || null,
        facebook_url: formData.facebook_url || null,
        linkedin_url: formData.linkedin_url || null,
        category_id: formData.category_id,
        country_id: country_id, // Send country_id instead of country name
        wilaya_id: formData.wilaya_id,
        city: formData.city || null,
        logo: formData.logo // Include logo file
      };

      // Update training center profile
      const response = await authService.updateTrainingCenter(updateData);
      
      if (response) {
        setSuccessMessage(t('auth.success.profileCompleted'));
        
        // Redirect to training center sign-in page after successful completion
        setTimeout(() => {
          navigate('/auth/training-center');
        }, 2000);
      }
      
    } catch (error: any) {
      // Handle specific error cases
      if (error.message?.includes('Session expirée')) {
        setErrors({ general: 'Votre session a expiré. Veuillez vous reconnecter.' });
        // Redirect to login after a delay
        setTimeout(() => {
          navigate('/auth/training-center');
        }, 3000);
      } else if (error.message?.includes('Accès refusé')) {
        setErrors({ general: 'Accès refusé. Vérifiez vos permissions.' });
      } else if (error.message?.includes('Erreur serveur')) {
        setErrors({ general: 'Erreur serveur. Veuillez réessayer plus tard.' });
      } else {
        setErrors({ general: error.message || 'Erreur lors de la mise à jour du profil. Veuillez réessayer.' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = () => {
    // Allow user to skip this step and complete later
    navigate('/auth/training-center');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-surface via-white to-gray-50">
      <div className="max-w-4xl mx-auto px-6 py-8">
        
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-6">
            <img src={logo} alt="PHINLEX" className="h-10 w-auto" />
            <span className="ml-3 text-2xl font-serif font-bold text-gray-900">PHINLEX</span>
          </div>
          
          <div className="max-w-2xl mx-auto">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Complétez votre profil
            </h1>
            <p className="text-lg text-gray-600">
              Dernière étape pour activer votre centre de formation sur PHINLEX
            </p>
          </div>
        </div>

        {/* Progress Indicator */}
        <div className="mb-8">
          <div className="flex items-center justify-center space-x-4">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-brand-primary text-white rounded-full flex items-center justify-center text-sm font-bold">
                <CheckCircle className="h-5 w-5" />
              </div>
              <span className="ml-2 text-sm font-medium text-brand-primary">Compte créé</span>
            </div>
            <div className="w-12 h-0.5 bg-brand-primary"></div>
            <div className="flex items-center">
              <div className="w-8 h-8 bg-brand-primary text-white rounded-full flex items-center justify-center text-sm font-bold">
                <CheckCircle className="h-5 w-5" />
              </div>
              <span className="ml-2 text-sm font-medium text-brand-primary">Email vérifié</span>
            </div>
            <div className="w-12 h-0.5 bg-brand-primary"></div>
            <div className="flex items-center">
              <div className="w-8 h-8 bg-brand-primary text-white rounded-full flex items-center justify-center text-sm font-bold">3</div>
              <span className="ml-2 text-sm font-medium text-brand-primary">Profil complet</span>
            </div>
          </div>
        </div>

        {/* Main Form Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-black/5 p-8">
          
          {/* Success Message */}
          {successMessage && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="mb-6 p-4 bg-brand-primary/10 border border-brand-primary/20 rounded-lg flex items-center gap-3"
            >
              <CheckCircle className="h-5 w-5 text-brand-primary" />
              <p className="text-brand-primary font-medium">{successMessage}</p>
            </motion.div>
          )}

          {/* General Error */}
          {errors.general && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-red-600" />
              <p className="text-red-800">{errors.general}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8">
            
            {/* Logo Upload Section */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Building2 className="h-5 w-5 text-brand-primary" />
                Logo du centre (optionnel)
              </h3>
              
              <div className="flex items-start gap-6">
                <div className="flex-shrink-0">
                  {logoPreview ? (
                    <div className="relative">
                      <img 
                        src={logoPreview} 
                        alt="Logo preview" 
                        className="w-24 h-24 object-cover rounded-lg border-2 border-gray-200"
                      />
                      <button
                        type="button"
                        onClick={removeLogo}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                      <Upload className="h-8 w-8 text-gray-400" />
                    </div>
                  )}
                </div>
                
                <div className="flex-1">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoChange}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-brand-primary/10 file:text-brand-primary hover:file:bg-brand-primary/20"
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    PNG, JPG ou GIF jusqu'à 5MB. Recommandé: 400x400px
                  </p>
                </div>
              </div>
            </div>

            {/* About Section */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FileText className="h-5 w-5 text-brand-primary" />
                Description de votre centre *
              </h3>
              <textarea
                value={formData.about}
                onChange={(e) => handleInputChange('about', e.target.value)}
                rows={6}
                className={`w-full px-4 py-3 rounded-lg border transition-colors resize-none ${
                  errors.about
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:border-brand-primary focus:ring-brand-primary/20'
                } focus:outline-none focus:ring-2`}
                placeholder="Présentez votre centre de formation : votre mission, vos valeurs, votre expertise, vos certifications, votre équipe pédagogique..."
              />
              <div className="flex justify-between items-center mt-2">
                {errors.about && (
                  <p className="text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    {errors.about}
                  </p>
                )}
                <p className="text-sm text-gray-500 ml-auto">
                  {formData.about.length}/50 caractères
                </p>
              </div>
            </div>

            {/* Location Section */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <MapPin className="h-5 w-5 text-brand-primary" />
                Localisation *
              </h3>
              
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Pays *
                  </label>
                  <select
                    value={formData.country}
                    onChange={(e) => handleCountryChange(e.target.value)}
                    disabled={loadingCountries}
                    className={`w-full px-4 py-3 rounded-lg border transition-colors ${
                      errors.country
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:border-brand-primary focus:ring-brand-primary/20'
                    } focus:outline-none focus:ring-2 ${loadingCountries ? 'bg-gray-100' : ''}`}
                  >
                    <option value="">
                      {loadingCountries ? 'Chargement des pays...' : 'Sélectionner un pays'}
                    </option>
                    {countries.map(country => (
                      <option key={country.id} value={country.name}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                  {errors.country && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="h-4 w-4" />
                      {errors.country}
                    </p>
                  )}
                </div>

                {/* Wilaya Selection - Only show if Algeria is selected */}
                {formData.country === 'Algeria' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Wilaya *
                  </label>
                  <select
                    value={formData.wilaya_id || ''}
                    onChange={(e) => handleInputChange('wilaya_id', e.target.value ? parseInt(e.target.value) : '')}
                      disabled={loadingWilayas}
                    className={`w-full px-4 py-3 rounded-lg border transition-colors ${
                      errors.wilaya_id
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:border-brand-primary focus:ring-brand-primary/20'
                      } focus:outline-none focus:ring-2 ${loadingWilayas ? 'bg-gray-100' : ''}`}
                    >
                      <option value="">
                        {loadingWilayas ? 'Chargement des wilayas...' : 'Sélectionner une wilaya'}
                      </option>
                      {wilayas.map((wilaya) => (
                        <option key={wilaya.id} value={wilaya.id}>
                          {wilaya.id.toString().padStart(2, '0')} - {wilaya.name}
                      </option>
                    ))}
                  </select>
                  {errors.wilaya_id && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="h-4 w-4" />
                      {errors.wilaya_id}
                    </p>
                  )}
                </div>
                )}

                {/* City Input - Only show if not Algeria */}
                {formData.country && formData.country !== 'Algeria' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ville *
                  </label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    className={`w-full px-4 py-3 rounded-lg border transition-colors ${
                      errors.city
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:border-brand-primary focus:ring-brand-primary/20'
                    } focus:outline-none focus:ring-2`}
                    placeholder="Nom de la ville"
                  />
                  {errors.city && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="h-4 w-4" />
                      {errors.city}
                    </p>
                  )}
                </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Adresse complète *
                </label>
                <textarea
                  value={formData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  rows={3}
                  className={`w-full px-4 py-3 rounded-lg border transition-colors resize-none ${
                    errors.address
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                      : 'border-gray-300 focus:border-brand-primary focus:ring-brand-primary/20'
                  } focus:outline-none focus:ring-2`}
                  placeholder={formData.country === 'Algeria' 
                    ? "Ex: 123 Rue de la République, Alger, Algérie" 
                    : "Ex: 123 Main Street, City, Country"
                  }
                />
                {errors.address && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    {errors.address}
                  </p>
                )}
              </div>
            </div>

            {/* Categories of Expertise */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Catégories d'expertise *
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Sélectionnez la catégorie principale dans laquelle votre centre propose des formations
              </p>
              
              {loadingCategories ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
                  <span className="ml-3 text-sm text-gray-500">Chargement des catégories...</span>
                </div>
              ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {categories.map((category) => (
                  <label
                      key={category.id}
                      className={`flex items-start p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        formData.category_id === category.id
                        ? 'border-brand-primary bg-brand-primary/10'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="category"
                      value={category.id}
                      checked={formData.category_id === category.id}
                      onChange={() => handleCategoryChange(category.id)}
                      className="sr-only"
                    />
                      <div className={`w-4 h-4 rounded-full border-2 mr-3 mt-0.5 flex items-center justify-center ${
                        formData.category_id === category.id
                        ? 'border-brand-primary bg-brand-primary'
                        : 'border-gray-300'
                    }`}>
                        {formData.category_id === category.id && (
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      )}
                    </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-700">
                          {category.name_french}
                        </div>
                        <div className="text-xs text-gray-500">
                          {category.name_english}
                        </div>
                      </div>
                  </label>
                ))}
              </div>
              )}
              
              {errors.category_id && (
                <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  {errors.category_id}
                </p>
              )}
              
              <p className="mt-2 text-sm text-gray-500">
                {formData.category_id ? '1 catégorie sélectionnée' : 'Aucune catégorie sélectionnée'}
              </p>
            </div>

            {/* Online Presence Section */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Globe className="h-5 w-5 text-brand-primary" />
                Présence en ligne (optionnel)
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Site web
                  </label>
                  <input
                    type="url"
                    value={formData.website}
                    onChange={(e) => handleInputChange('website', e.target.value)}
                    className={`w-full px-4 py-3 rounded-lg border transition-colors ${
                      errors.website
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:border-brand-primary focus:ring-brand-primary/20'
                    } focus:outline-none focus:ring-2`}
                    placeholder="https://www.votre-centre.com"
                  />
                  {errors.website && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="h-4 w-4" />
                      {errors.website}
                    </p>
                  )}
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Facebook className="h-4 w-4 text-blue-600" />
                      Facebook
                    </label>
                    <input
                      type="url"
                      value={formData.facebook_url}
                      onChange={(e) => handleInputChange('facebook_url', e.target.value)}
                      className={`w-full px-4 py-3 rounded-lg border transition-colors ${
                        errors.facebook_url
                          ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                          : 'border-gray-300 focus:border-brand-primary focus:ring-brand-primary/20'
                      } focus:outline-none focus:ring-2`}
                      placeholder="https://facebook.com/votre-profil"
                    />
                    {errors.facebook_url && (
                      <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="h-4 w-4" />
                        {errors.facebook_url}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Linkedin className="h-4 w-4 text-blue-700" />
                      LinkedIn
                    </label>
                    <input
                      type="url"
                      value={formData.linkedin_url}
                      onChange={(e) => handleInputChange('linkedin_url', e.target.value)}
                      className={`w-full px-4 py-3 rounded-lg border transition-colors ${
                        errors.linkedin_url
                          ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                          : 'border-gray-300 focus:border-brand-primary focus:ring-brand-primary/20'
                      } focus:outline-none focus:ring-2`}
                      placeholder="https://linkedin.com/in/votre-profil"
                    />
                    {errors.linkedin_url && (
                      <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="h-4 w-4" />
                        {errors.linkedin_url}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={handleSkip}
                className="flex-1 py-3 px-6 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Compléter plus tard
              </button>
              
              <button
                type="submit"
                disabled={isLoading}
                className="flex-1 bg-brand-primary text-white py-3 px-6 rounded-lg font-semibold hover:bg-brand-dark transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Finalisation...
                  </>
                ) : (
                  <>
                    Activer mon centre
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Help Text */}
        <div className="text-center mt-8">
          <p className="text-sm text-gray-500">
            Besoin d'aide ? Contactez notre équipe à{' '}
            <a href="mailto:support@phinlex.com" className="text-brand-primary hover:underline">
              support@phinlex.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default TrainingCenterCompleteProfilePage;
